/**
 * In-page sign-in sheet (opened by requireAccount). Keeps the editor and the
 * photo already uploaded on screen: the user requests a magic link, opens it
 * (in dev: one tap, in a new tab), and this tab becomes signed in on its own
 * via the `storage` event handled in state/auth.ts.
 */
import { api, ApiError } from '../api/client.js';
import { authChanged, hasAccount } from '../state/auth.js';
import { refreshCredits } from '../state/credits.js';
import { toast } from './toast.js';
import { bindGoogleButton, googleButtonHtml } from '../components/googleButton.js';

let openSheet: HTMLElement | null = null;

function esc(v: string): string {
  return v.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

export function closeSignInSheet(): void {
  openSheet?.remove();
  openSheet = null;
}

export function openSignInSheet(
  reason: string,
  note = 'Your photo is saved. Sign in to continue — no password needed.',
): void {
  if (openSheet) return;
  const el = document.createElement('div');
  el.className = 'signin-backdrop';
  el.innerHTML = `
    <div class="signin-sheet" role="dialog" aria-modal="true" aria-labelledby="si-title">
      <button class="signin-x" type="button" aria-label="Close" data-si="close">✕</button>
      <h2 id="si-title" class="page-title" style="font-size: var(--fs-xl);">${esc(reason)}</h2>
      <p class="muted">${esc(note)}</p>
      <div class="stack" data-si="google">${googleButtonHtml()}</div>
      <form class="stack" data-si="form" novalidate>
        <input class="input" type="email" name="email" placeholder="you@example.com"
               autocomplete="email" required aria-label="Email" />
        <button class="btn btn-primary btn-block" type="submit">Send sign-in link</button>
      </form>
      <div class="stack" data-si="sent" hidden></div>
    </div>`;
  document.body.appendChild(el);
  openSheet = el;

  const form = el.querySelector<HTMLFormElement>('[data-si="form"]')!;
  const sent = el.querySelector<HTMLElement>('[data-si="sent"]')!;
  const input = form.querySelector<HTMLInputElement>('input')!;
  bindGoogleButton(el);

  el.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t === el || t.closest('[data-si="close"]')) closeSignInSheet();
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const email = input.value.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast('Please enter a valid email address.');
      return;
    }
    const btn = form.querySelector('button')!;
    btn.disabled = true;
    api
      .requestMagicLink(email)
      .then((res) => {
        form.hidden = true;
        el.querySelector<HTMLElement>('[data-si="google"]')!.hidden = true;
        sent.hidden = false;
        sent.innerHTML = `
          <p>We sent a sign-in link to <strong class="credit">${esc(email)}</strong>. Open it, then come back to this tab — you will be signed in automatically.</p>
          ${
            res.devMagicUrl
              ? `<a class="btn btn-primary btn-block" href="${esc(res.devMagicUrl)}" target="_blank" rel="noopener">Open sign-in link</a>
                 <p class="muted" style="font-size: var(--fs-xs);">Dev mode: no email provider is configured, so no email is sent.</p>`
              : ''
          }`;
      })
      .catch((err: unknown) => {
        btn.disabled = false;
        toast(err instanceof ApiError ? err.message : 'Could not send the link. Check your connection and try again.');
      });
  });
}

// When this tab becomes signed in (another tab opened the link), close the sheet.
authChanged.on(() => {
  if (openSheet && hasAccount()) {
    closeSignInSheet();
    void refreshCredits().catch(() => undefined);
    toast('Signed in — tap Generate to continue.');
  }
});
